import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, FilterChip, Input, PageHeader, QueryState } from '@yhelp/ui';
import type { Ticket, TicketClassification, TicketStatus } from '@yhelp/shared-types';
import { DashboardLayout, PrimaryLinkButton } from '@/templates/DashboardLayout';
import { useAuthorizedFetch } from '@/lib/useAuthorizedFetch';
import { statusLabel } from '@/lib/ticket-status';
import { productLabel } from '@/lib/ticket-meta';
import { ClassificationBadge } from '@/components/molecules/ClassificationBadge';

const STATUS_FILTERS: Array<{ value: '' | TicketStatus; label: string }> = [
  { value: '', label: 'Todos' },
  { value: 'aberto', label: 'Abertos' },
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'em_espera', label: 'Em espera' },
  { value: 'resolvido', label: 'Resolvidos' },
  { value: 'fechado', label: 'Fechados' },
];

export function TicketsPage() {
  const authorizedFetch = useAuthorizedFetch();
  const [statusFilter, setStatusFilter] = useState<'' | TicketStatus>('');
  const [search, setSearch] = useState('');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['tickets', statusFilter, search],
    queryFn: () => {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (search.trim()) params.set('q', search.trim());
      const qs = params.toString();
      return authorizedFetch<{ data: Ticket[] }>(`/v1/tickets${qs ? `?${qs}` : ''}`);
    },
  });

  const hasFilters = Boolean(statusFilter || search.trim());
  const isEmpty = !isLoading && !isError && !data?.data.length;

  return (
    <DashboardLayout
      pageDescription="Gerencie e acompanhe todos os seus chamados"
      primaryAction={
        <span data-tour="ticket-create-btn">
          <PrimaryLinkButton to="/tickets/new">Novo chamado</PrimaryLinkButton>
        </span>
      }
    >
      <PageHeader title="Chamados" description="Filtre por status ou busque por título e número" />
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2" data-tour="tickets-list">
          {STATUS_FILTERS.map((f) => (
            <FilterChip
              key={f.label}
              selected={statusFilter === f.value}
              onClick={() => setStatusFilter(f.value)}
            >
              {f.label}
            </FilterChip>
          ))}
        </div>
        <Input
          className="max-w-xs"
          placeholder="Buscar por título ou número…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Buscar chamados"
        />
      </div>

      <QueryState
        isLoading={isLoading}
        isError={isError}
        isEmpty={isEmpty}
        errorProps={{
          title: 'Erro ao carregar chamados',
          action: (
            <Button variant="secondary" onClick={() => void refetch()}>
              Tentar novamente
            </Button>
          ),
        }}
        emptyProps={{
          variant: hasFilters ? 'search' : 'default',
          title: hasFilters ? 'Nenhum resultado' : 'Nenhum chamado',
          description: hasFilters
            ? 'Ajuste os filtros ou limpe a busca.'
            : 'Crie o primeiro chamado para começar.',
          action: hasFilters ? (
            <Button
              variant="secondary"
              onClick={() => {
                setStatusFilter('');
                setSearch('');
              }}
            >
              Limpar filtros
            </Button>
          ) : (
            <PrimaryLinkButton to="/tickets/new">Novo chamado</PrimaryLinkButton>
          ),
        }}
      >
        <ul className="divide-y divide-neutral-200 surface-card overflow-hidden dark:divide-neutral-800">
          {data?.data.map((t, i) => (
            <li
              key={t.id}
              data-tour={i === 0 ? 'ticket-row-first' : undefined}
              className="list-stagger"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <Link
                to="/tickets/$ticketId"
                params={{ ticketId: t.id }}
                className="flex flex-col gap-2 px-4 py-3 transition-colors duration-fast hover:bg-neutral-50 motion-reduce:transition-none dark:hover:bg-neutral-800 sm:flex-row sm:items-center sm:gap-4"
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <span className="shrink-0 font-mono text-sm text-muted">#{t.number}</span>
                  <span className="min-w-0 flex-1 font-medium text-yhelp-black dark:text-white">
                    {t.title}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                  {t.classification ? (
                    <ClassificationBadge
                      classification={t.classification as TicketClassification}
                    />
                  ) : null}
                  {t.product ? (
                    <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
                      {productLabel(t.product)}
                    </span>
                  ) : null}
                  <Badge variant="status" status={t.status}>
                    {statusLabel(t.status)}
                  </Badge>
                  <Badge variant="priority" priority={t.priority}>
                    {t.priority}
                  </Badge>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </QueryState>
    </DashboardLayout>
  );
}
